import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Select, MenuItem, IconButton, MenuProps, ListItemText, FormControl } from '@mui/material';
import { React } from 'Imports';
import { faChevronLeft, faChevronRight, faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { isEncompass } from '$Utilities/integrationPlatform';
import classNames from 'classnames';
import * as scssStyles from '$CSS/settings.scss';

const styles = require('./CalendarNavigation.scss') as {
    container: string;
    navigationButton: string;
    navigationButtonEncompass: string;
    navigationButtonGeotab: string;
    dropdownIcon: string;
    previousButton: string;
    nextButton: string;
    selectContainer: string;
    selectValueGeotab: string;
    selectValueEncompass: string;
    scrollAreaGeotab: string;
    scrollAreaEncompass: string;
    navigationContainer: string;
    navigationContainerEncompass: string;
    navigationContainerGeotab: string;
    navigationButtonContainer: string;
    controlsContainer: string;
    monthSelectContainer: string;
    yearSelectContainer: string;
    formControl: string;
    selectElement: string;
    menuItem: string;
    listItemText: string;
    nextButtonContainer: string;
    menuPaper: string;
    selectFocused: string;
    selectUnfocused: string;
};

interface ICalendarNavigationProps {
    currentMonth: number;
    currentYear: number;
    onMonthChange: (month: number) => void;
    onYearChange: (year: number) => void;
    onPreviousMonth: () => void;
    onNextMonth: () => void;
}

export const CalendarNavigation: React.FC<ICalendarNavigationProps> = ({
    currentMonth,
    currentYear,
    onMonthChange,
    onYearChange,
    onPreviousMonth,
    onNextMonth,
}) => {
    // Track open state for each dropdown
    const [monthOpen, setMonthOpen] = React.useState(false);
    const [yearOpen, setYearOpen] = React.useState(false);
    const [monthHasFocus, setMonthHasFocus] = React.useState(false);
    const [yearHasFocus, setYearHasFocus] = React.useState(false);

    // Month names for the dropdown
    const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    const currentYearActual = new Date().getFullYear();
    const yearOptions = Array.from({ length: 10 }, (_, i) => currentYearActual - 9 + i);

    // Menu props to match SingleSelectDropdownMenu styling
    const getMenuProps = (): Partial<MenuProps> => ({
        transformOrigin: { horizontal: 'left', vertical: 'top' },
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        container: () => document.getElementById('date-range-picker-calendar-navigation'),
        disablePortal: true,
        slotProps: {
            paper: {
                className: classNames(isEncompass ? styles.scrollAreaEncompass : styles.scrollAreaGeotab, styles.menuPaper),
            },
        },
        sx: { zIndex: 10000 },
    });

    const handleMonthChange = (event: any) => {
        onMonthChange(event.target.value);
    };

    const handleYearChange = (event: any) => {
        onYearChange(event.target.value);
    };

    const navigationButtonClass = classNames(
        styles.navigationButton,
        isEncompass ? styles.navigationButtonEncompass : styles.navigationButtonGeotab,
    );

    // Get today's month and year
    const today = new Date();
    const isCurrentMonthAndYear = currentMonth === today.getMonth() && currentYear === today.getFullYear();

    return (
        <Box
            className={classNames(
                styles.navigationContainer,
                isEncompass ? styles.navigationContainerEncompass : styles.navigationContainerGeotab,
            )}
            id={'date-range-picker-calendar-navigation'}
            data-testid={'date-range-picker-calendar-navigation'}
        >
            <Box className={classNames(styles.navigationButtonContainer, isEncompass ? 'encompass' : 'geotab')}>
                <IconButton onClick={onPreviousMonth} disableRipple className={classNames(navigationButtonClass, styles.previousButton)}>
                    <FontAwesomeIcon size={'xs'} icon={faChevronLeft} />
                </IconButton>
            </Box>
            <Box className={styles.controlsContainer}>
                <Box className={classNames(styles.monthSelectContainer, isEncompass ? 'encompass' : 'geotab')}>
                    <FormControl
                        className={classNames(styles.formControl, isEncompass ? 'monthEncompass' : 'monthGeotab')}
                        focused={monthHasFocus}
                    >
                        <Select
                            value={currentMonth}
                            onChange={handleMonthChange}
                            size="small"
                            variant="outlined"
                            MenuProps={getMenuProps()}
                            open={monthOpen}
                            onOpen={() => {
                                setMonthOpen(true);
                                setMonthHasFocus(true);
                            }}
                            onClose={() => {
                                setMonthOpen(false);
                                setMonthHasFocus(false);
                            }}
                            inputProps={isEncompass ? { sx: { padding: '0 !important' } } : {}}
                            renderValue={(value) => (
                                <Box className={isEncompass ? styles.selectValueEncompass : styles.selectValueGeotab}>
                                    {monthNames[value]}
                                </Box>
                            )}
                            autoFocus={false}
                            className={classNames(
                                styles.selectElement,
                                isEncompass ? 'encompass' : 'geotab',
                                monthHasFocus
                                    ? classNames(styles.selectFocused, isEncompass ? 'encompass' : 'geotab')
                                    : classNames(styles.selectUnfocused, isEncompass ? 'encompass' : 'geotab'),
                            )}
                            sx={{
                                fontSize: '14px',
                                cursor: 'pointer',
                                height: isEncompass ? '30px' : '34px',
                                width: !isEncompass ? '138px' : 'auto',
                                '&:focus': { backgroundColor: 'transparent' },
                            }}
                            IconComponent={() => (
                                <Box
                                    className={styles.dropdownIcon}
                                    onMouseDown={(e) => {
                                        if (monthOpen) {
                                            setMonthOpen(false);
                                            setMonthHasFocus(false);
                                        } else {
                                            setMonthOpen(true);
                                            setMonthHasFocus(true);
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={monthOpen ? faCaretUp : faCaretDown}
                                        color={scssStyles.videoEventFilterSelectIconColor}
                                    />
                                </Box>
                            )}
                        >
                            {monthNames.map((month, index) => (
                                <MenuItem disableRipple={true} key={index} value={index} className={styles.menuItem}>
                                    <ListItemText
                                        primary={month}
                                        primaryTypographyProps={{
                                            noWrap: true,
                                            fontWeight: 400,
                                            textOverflow: 'ellipsis',
                                            color: scssStyles.customColor2,
                                            className: classNames(styles.listItemText, isEncompass ? 'encompass' : 'geotab'),
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box className={classNames(styles.yearSelectContainer, isEncompass ? 'encompass' : 'geotab')}>
                    <FormControl
                        className={classNames(styles.formControl, isEncompass ? 'yearEncompass' : 'yearGeotab')}
                        focused={yearHasFocus}
                    >
                        <Select
                            value={currentYear}
                            onChange={handleYearChange}
                            size="small"
                            variant="outlined"
                            MenuProps={getMenuProps()}
                            open={yearOpen}
                            onOpen={() => {
                                setYearOpen(true);
                                setYearHasFocus(true);
                            }}
                            onClose={() => {
                                setYearOpen(false);
                                setYearHasFocus(false);
                            }}
                            inputProps={isEncompass ? { sx: { padding: '0 !important' } } : {}}
                            renderValue={(value) => (
                                <Box className={isEncompass ? styles.selectValueEncompass : styles.selectValueGeotab}>{value}</Box>
                            )}
                            autoFocus={false}
                            className={classNames(
                                styles.selectElement,
                                isEncompass ? 'encompass' : 'geotab',
                                yearHasFocus
                                    ? classNames(styles.selectFocused, isEncompass ? 'encompass' : 'geotab')
                                    : classNames(styles.selectUnfocused, isEncompass ? 'encompass' : 'geotab'),
                            )}
                            sx={{
                                fontSize: '14px',
                                cursor: 'pointer',
                                height: isEncompass ? '30px' : '34px',
                                width: !isEncompass ? '76px' : 'auto',
                                '&:focus': { backgroundColor: 'transparent' },
                            }}
                            IconComponent={() => (
                                <Box
                                    className={styles.dropdownIcon}
                                    onMouseDown={(e) => {
                                        if (yearOpen) {
                                            setYearOpen(false);
                                            setYearHasFocus(false);
                                        } else {
                                            setYearOpen(true);
                                            setYearHasFocus(true);
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={yearOpen ? faCaretUp : faCaretDown}
                                        color={scssStyles.videoEventFilterSelectIconColor}
                                    />
                                </Box>
                            )}
                        >
                            {yearOptions.map((year, index) => (
                                <MenuItem disableRipple={true} key={index} value={year} className={styles.menuItem}>
                                    <ListItemText
                                        primary={year}
                                        primaryTypographyProps={{
                                            noWrap: true,
                                            fontWeight: 400,
                                            textOverflow: 'ellipsis',
                                            color: scssStyles.customColor2,
                                            className: classNames(styles.listItemText, isEncompass ? 'encompass' : 'geotab'),
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>
            <Box
                className={classNames(
                    styles.nextButtonContainer,
                    isEncompass ? 'encompass' : 'geotab',
                    isCurrentMonthAndYear ? 'disabled' : '',
                )}
            >
                <IconButton onClick={onNextMonth} disableRipple className={classNames(navigationButtonClass, styles.nextButton)}>
                    <FontAwesomeIcon size={'xs'} icon={faChevronRight} />
                </IconButton>
            </Box>
        </Box>
    );
};

export default CalendarNavigation;
